import clsx from 'clsx';
import type { Control, FieldValues, Path } from 'react-hook-form';

interface Options {
  pattern?: {
    value: RegExp;
    message: string;
  };
  valueAsDate?: boolean;
  [key: string]: any;
}

type InputProps<T extends FieldValues> = {
  field?: Path<T>;
  options?: Options;
  control?: Control<T>;
  type:
    | 'text'
    | 'number'
    | 'email'
    | 'password'
    | 'color'
    | 'checkbox'
    | 'radio'
    | 'url';
  placeholder?: string;
  parentClassName?: string;
  error?: string;
  errorClassName?: string;
} & React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
>;

export function InputT<T extends FieldValues>({
  type,
  field,
  options,
  control,
  placeholder,
  style,
  parentClassName,
  error,
  errorClassName = '',
  ...restParams
}: InputProps<T>) {
  // We need to extract the disabled property from the options object, because this causes a lot of issues with the field validation if the field is disabled.
  const { pattern, valueAsDate, disabled, ...registerOptions } = options || {};
  const { className, ...rest } = restParams;

  let inputDefaultStyle = [
    'block',
    'rounded-md',
    'py-1.5',
    'px-2.5',
    'text-xs',
    'leading-5',
    'outline-none',
    'font-normal',
    'text-gray-600',
    'placeholder:text-gray-300',
    disabled ? 'bg-gray-100' : 'bg-white',
    disabled ? 'opacity-70' : '',
    disabled ? 'cursor-default' : 'cursor-auto',
    disabled ? 'pointer-events-none' : '',
    disabled ? 'italic' : '',
    disabled ? 'text-gray-200' : '',
  ];

  // If className contains a leading-* class, remove leading-6 from inputDefaultStyle, otherwise the leading-6 is always overwritten the leading-* class that is coming from the parent component
  if (className?.includes('leading-')) {
    inputDefaultStyle = inputDefaultStyle.filter(
      (style) => !style.startsWith('leading-'),
    );
  }

  if (!['checkbox', 'radio'].includes(type)) {
    inputDefaultStyle.push(
      'w-full',
      'ring-1',
      'ring-inset',
      error ? 'ring-red-base' : 'ring-gray-200',
      'focus:ring-1',
      'focus:ring-inset',
      error ? 'focus:ring-red-base' : 'focus:ring-primary-base',
    );
  } else {
    inputDefaultStyle.push('w-3', 'h-[23px]');
  }

  const divDefaultStyle = ['flex', 'flex-col'];

  if (!['checkbox', 'radio'].includes(type)) {
    divDefaultStyle.push('mb-2');
  }

  const registerOptionsWithPattern = {
    ...registerOptions,
    pattern: pattern
      ? {
          value: new RegExp(pattern.value),
          message: pattern.message.toString(),
        }
      : undefined,
  };

  const registerObject =
    field && control
      ? control.register(field, registerOptionsWithPattern)
      : {
          value: registerOptions?.value || '',
          onChange: registerOptions?.onChange || (() => {}),
        };

  return (
    <div className={clsx([...divDefaultStyle, parentClassName])}>
      <input
        id={field}
        className={clsx([...inputDefaultStyle, className])}
        placeholder={placeholder}
        type={type}
        {...rest}
        {...registerObject}
      />
      {error && (
        <p
          className={clsx([
            'text-red-base',
            'text-pico',
            'mt-0',
            errorClassName,
          ])}>
          {error}
        </p>
      )}
    </div>
  );
}

export default InputT;
